
import { AIGenerationBaseDialog } from '@/shared/components/custom/AIGenerationBaseDialog'
import { useState, useEffect } from 'react'
import { ScreenplayOutlineGenerationOptions } from '../types'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import { Slider } from '@/shared/components/ui/slider'

interface ScreenplayOutlineGenerationDialogProps {
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    onGenerate: (options: ScreenplayOutlineGenerationOptions) => void
    storyContent: string
    isLoading?: boolean
}

export const ScreenplayOutlineGenerationDialog = ({
    isOpen,
    onOpenChange,
    onGenerate,
    storyContent,
    isLoading = false,
}: ScreenplayOutlineGenerationDialogProps) => {
    const [duration, setDuration] = useState('30 mins')
    const [sceneCount, setSceneCount] = useState(15)
    const [mode, setMode] = useState<'auto' | 'custom' | null>(null)

    // Reset state when dialog opens
    useEffect(() => {
        if (isOpen) {
            setMode(null)
            setDuration('30 mins')
            setSceneCount(15)
        }
    }, [isOpen])

    const handleGenerate = (m: 'auto' | 'custom') => {
        onGenerate({
            mode: m,
            story: storyContent,
            duration: m === 'custom' ? duration : undefined,
            sceneCount: m === 'custom' ? sceneCount : undefined,
            language: 'English', // default
        })
    }

    const reset = () => {
        setMode(null)
        onOpenChange(false)
    }

    return (
        <AIGenerationBaseDialog
            open={isOpen}
            onOpenChange={reset}
            dialogTitle="Generate Screenplay Outline"
            dialogDescription="Let AI craft the structure of your story. Choose between a quick blueprint or a custom-tailored outline."
            mode={mode}
            onModeChange={setMode}
            isLoading={isLoading}
            onAutoPilotClick={() => handleGenerate('auto')}
            autoPilotDescription="Let AI intelligently structure your screenplay based on the story content."
            customDirectorDescription="Fine-tune your screenplay's structure by specifying duration and the number of scenes."
            customStepDescription="Fine-tune your screenplay outline generation"
            onCustomGenerate={() => handleGenerate('custom')}
            customGenerateLabel="Generate Outline"
        >
            <div className="space-y-4">
                <div className="space-y-3">
                    <div className="flex justify-between">
                        <Label>Approximate Scene Count</Label>
                        <span className="text-sm text-muted-foreground font-mono">{sceneCount} scenes</span>
                    </div>
                    <Slider
                        value={[sceneCount]}
                        onValueChange={(v: number[]) => setSceneCount(v[0])}
                        min={1}
                        max={50}
                        step={1}
                        className="w-full"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="duration">Estimated Screenplay Duration</Label>
                    <div className="relative">
                        <Input
                            id="duration"
                            placeholder="e.g. 30 mins, 2 hours"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                        />
                    </div>
                    <p className="text-[0.8rem] text-muted-foreground">Helps AI estimate the overall pacing and length.</p>
                </div>
            </div>
        </AIGenerationBaseDialog>
    )
}
